import type { z } from "zod";
import type { RequestBuilder } from "./requestBuilder";
import { type Result, err, ok } from "neverthrow";

async function storytellFetch<
  PathParamsSchema extends z.ZodType | undefined = undefined,
  BodySchema extends z.ZodType | undefined = undefined,
  ResponseSchema extends z.ZodType | undefined = undefined,
>(
  builder: RequestBuilder<PathParamsSchema, BodySchema, ResponseSchema>,
  params: PathParamsSchema extends z.ZodType ? z.infer<PathParamsSchema> : undefined,
  body: BodySchema extends z.ZodType ? z.infer<BodySchema> : undefined,
): Promise<Result<ResponseSchema extends z.ZodType ? z.infer<ResponseSchema> : undefined, Error>> {
  const config = builder.build(params, body);
  if (config.isErr()) {
    return err(config.error);
  }

  const { request, responseSchema } = config.value;

  try {
    const response = await fetch(request);
    if (!response.ok) {
      console.error(`Failed to fetch: ${response.status}`);
      return err(new Error(`Failed to fetch: ${response.statusText}`));
    }

    if (!responseSchema) {
      return ok(undefined as ResponseSchema extends z.ZodType ? z.infer<ResponseSchema> : undefined);
    }

    const responseBody = await response.json();
    const parsed = responseSchema.safeParse(responseBody);

    if (!parsed.success) {
      const error = new Error(`Failed to parse response: ${parsed.error.message}`);
      console.error(error);
      return err(error);
    }

    return ok(parsed.data as ResponseSchema extends z.ZodType ? z.infer<ResponseSchema> : undefined);
  } catch (error) {
    console.error(error);
    return err(error as Error);
  }
}

// TODO: Add retry, timeout, etc options
export const useStorytellClient = () => {
  return {
    fetch: storytellFetch,
  };
};
